// Pathless layout route — every URL under it (e.g. /live-in-club) inherits
// the session-required guard. Tanstack Router's `_<name>.tsx` filename
// convention makes the prefix non-rendering: `/live-in-club` stays
// `/live-in-club`, not `/authenticated/live-in-club`.
//
// The shell-scope resolution work lives in the authentication bounded
// context (`application/resolve-authenticated-shell-scope.ts`). This route
// invokes it via a `createServerFn` so the call is isomorphic: the handler
// runs on the server (in-process on SSR, via an internal HTTP roundtrip on
// the client), keeping the returned `scope` shape consistent across the
// hydration boundary. That symmetry is what lets the drawer brand area
// render the Club name reliably; the older `createServerOnlyFn` +
// `typeof window` early-return pattern wiped the SSR-populated context
// after client hydration.
//
// The shell resolves ONLY the tenant scope (session + Club). Feature flags are
// a SEPARATE cached query (`evaluateFeatureFlagsServerFn`), warmed in this
// route's loader and read via `useFeatureFlag` — so the auth resolution and the
// flag evaluation cache and revalidate independently.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { warmFeatureFlags } from '~/_shared/feature-flags/evaluate-flags';
import { evaluateFeatureFlagsServerFn } from '~/_shared/feature-flags/evaluate-flags.fn';
import type { AuthenticatedShellScope } from '~/bounded-contexts/authentication/application/resolve-authenticated-shell-scope';
import { AuthenticatedRoute } from '~/bounded-contexts/authentication/ui/authenticated-route/authenticated-route';

export interface AuthenticatedShellData {
	scope: AuthenticatedShellScope;
}

/**
 * Query key for the cached authenticated-shell scope. Not partitioned by Club —
 * one session resolves to exactly one Club, and sign-out clears the whole cache
 * (see `authenticated-route.tsx`), so a signed-out user can't reuse it.
 */
export function authShellQueryKey() {
	return ['auth-shell'] as const;
}

const resolveAuthenticatedShell = createServerFn({ method: 'GET' }).handler(
	async (): Promise<AuthenticatedShellData | null> => {
		const { getServerAuthService } = await import(
			'~/bounded-contexts/authentication/composition.server'
		);
		const { getDb } = await import('~/_shared/db/composition');
		const { resolveAuthenticatedShellScope } = await import(
			'~/bounded-contexts/authentication/application/resolve-authenticated-shell-scope'
		);
		const scope = await resolveAuthenticatedShellScope(
			{ authService: getServerAuthService(), db: getDb() },
			getRequest(),
		);
		if (!scope) return null;
		return { scope };
	},
);

export const Route = createFileRoute('/_authenticated')({
	beforeLoad: async ({
		context,
		location,
	}): Promise<AuthenticatedShellData> => {
		const { queryClient } = context;
		// Cache the shell across client-side navigations: a warm cache returns
		// synchronously and skips the server round-trip; a cold one pays it once.
		// `revalidateIfStale` keeps the 60s staleTime meaningful — a stale scope
		// answers this nav from cache but refetches in the background, so a
		// server-side session change is picked up on a later nav instead of
		// never. Sign-out clears the whole cache, so a signed-out user can't
		// reuse a prior session's scope.
		const data = await queryClient.ensureQueryData({
			queryKey: authShellQueryKey(),
			queryFn: () => resolveAuthenticatedShell(),
			staleTime: 60_000,
			revalidateIfStale: true,
		});
		if (!data) {
			// Drop the cached null so a subsequent client-side login doesn't read a
			// stale "no session" and bounce straight back to /login.
			queryClient.removeQueries({ queryKey: authShellQueryKey() });
			throw redirect({
				to: '/login',
				search: { redirect: location.href } as { redirect?: string },
			});
		}
		return { scope: data.scope };
	},
	loader: ({ context }) =>
		// Warm the per-Club feature-flag cache. Invariant (enforced by
		// `warmFeatureFlags`): the authenticated shell never renders without a
		// real flag map — SSR awaits (dehydrates, no hydration flash), a COLD
		// client cache awaits too (once per session, right after login, so the
		// first client-side entry paints the nav from real flags rather than
		// the dark defaults), and a warm client cache costs nothing. A rejected
		// evaluation caches nothing and never fails the route load.
		warmFeatureFlags(context.queryClient, context.scope.club.id, () =>
			evaluateFeatureFlagsServerFn(),
		),
	component: AuthenticatedRoute,
});

export type { AuthenticatedShellScope };
